/**
 * useOrderBookNav — the order-book drill's place in the URL. The top bar owns SCOPE
 * (site · cadence · window, useScope); this owns the surface's IN-CONTEXT NAVIGATION,
 * which works-list §3.3 explicitly allows a page to keep: where in the book you are
 * (`ob`), what you are breaking it down by (`ob_axis`), and which cost basis you are
 * reading (`ob_basis`).
 *
 * Putting it in the URL is the point: the browser back button walks the drill, and a
 * customer at a level is a link somebody can paste into an email. Round-trip safe —
 * any malformed param parses to the clean default, never a crash — and every write goes
 * through the functional form of setSearchParams so it cannot race the scope writer.
 */

import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { PathSegment } from '../types/metrics'
import type { Basis } from '../viewmodels/orderBook'

/** The breakdown grains this book can offer. Not sectors — we have no sector dimension;
 * these are the axes the order-book fact actually carries. */
export const AXES = ['customer', 'part', 'month'] as const
export type Axis = (typeof AXES)[number]

/** The two questions this surface answers, kept apart the way the reference keeps them:
 * how margin moves over the delivery calendar, and who it is made of right now. Stacking
 * both on one page made a very long scroll and buried the drill. */
export const TABS = ['trend', 'breakdown'] as const
export type Tab = (typeof TABS)[number]

export const AXIS_LABEL: Record<Axis, string> = {
  customer: 'Customer',
  part: 'Part',
  month: 'Due month',
}

const PATH_KEY = 'ob'
const AXIS_KEY = 'ob_axis'
const BASIS_KEY = 'ob_basis'
const MONTH_KEY = 'ob_month'
const TAB_KEY = 'ob_tab'
const KEYS = [PATH_KEY, AXIS_KEY, BASIS_KEY, MONTH_KEY, TAB_KEY]
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/

export interface OrderBookNav {
  path: PathSegment[]
  axis: Axis
  basis: Basis
  /** A month picked off the timeline, holding the drill to it. null = the whole window. */
  month: string | null
  tab: Tab
  setPath: (path: PathSegment[]) => void
  descend: (segment: PathSegment) => void
  setAxis: (axis: Axis) => void
  setBasis: (basis: Basis) => void
  setMonth: (month: string | null) => void
  setTab: (tab: Tab) => void
}

/** `ob` param → typed crumbs; ANY malformed shape → no drill (never a crash). */
function parsePath(raw: string | null): PathSegment[] {
  if (!raw) return []
  try {
    const loaded: unknown = JSON.parse(raw)
    if (!Array.isArray(loaded)) return []
    const segments: PathSegment[] = []
    for (const item of loaded) {
      const seg = item as { level?: unknown; id?: unknown }
      if (typeof seg?.level !== 'string' || !seg.level || typeof seg?.id !== 'string' || !seg.id)
        return []
      segments.push({ level: seg.level, id: seg.id })
    }
    return segments
  } catch {
    return []
  }
}

interface NavState {
  path: PathSegment[]
  axis: Axis
  basis: Basis
  month: string | null
  tab: Tab
}

function write(previous: URLSearchParams, next: NavState): URLSearchParams {
  const params = new URLSearchParams(previous)
  KEYS.forEach((key) => params.delete(key))
  if (next.path.length) params.set(PATH_KEY, JSON.stringify(next.path))
  if (next.axis !== 'customer') params.set(AXIS_KEY, next.axis)
  if (next.basis !== 'actual') params.set(BASIS_KEY, next.basis)
  if (next.month) params.set(MONTH_KEY, next.month)
  if (next.tab !== 'trend') params.set(TAB_KEY, next.tab)
  return params
}

const readTab = (params: URLSearchParams): Tab =>
  TABS.includes(params.get(TAB_KEY) as Tab) ? (params.get(TAB_KEY) as Tab) : 'trend'

const readMonth = (params: URLSearchParams): string | null => {
  const raw = params.get(MONTH_KEY)
  return raw && MONTH_RE.test(raw) ? raw : null
}

type SetParams = ReturnType<typeof useSearchParams>[1]

/** The write half, split out to hold the CD-1 function budget in useOrderBookNav.
 * Every setter reads the CURRENT params inside the updater, so two writers on the same
 * tick cannot clobber each other (the race the capacity surface hit). */
function useNavSetters(axis: Axis, basis: Basis, setSearchParams: SetParams) {
  const at = (p: URLSearchParams): NavState => ({
    path: parsePath(p.get(PATH_KEY)), axis, basis, month: readMonth(p), tab: readTab(p),
  })
  const setPath = useCallback(
    (path: PathSegment[]) => setSearchParams((p) => write(p, { ...at(p), path })),
    [setSearchParams, axis, basis]
  )
  const descend = useCallback(
    (segment: PathSegment) =>
      setSearchParams((p) => write(p, { ...at(p), path: [...parsePath(p.get(PATH_KEY)), segment] })),
    [setSearchParams, axis, basis]
  )
  // The axis and a held month both re-cut what sits BENEATH entity, so crumbs at or below
  // the cut stop addressing anything and are dropped. An entity crumb sits above it and
  // survives, so re-cutting keeps you where you are instead of throwing you to the top.
  const aboveTheCut = (p: URLSearchParams): PathSegment[] =>
    parsePath(p.get(PATH_KEY)).filter((seg) => seg.level === 'entity')
  const setAxis = useCallback(
    (next: Axis) => setSearchParams((p) => write(p, { ...at(p), axis: next, path: aboveTheCut(p) })),
    [setSearchParams, axis, basis]
  )
  const setBasis = useCallback(
    (next: Basis) => setSearchParams((p) => write(p, { ...at(p), basis: next })),
    [setSearchParams, axis, basis]
  )
  // Picking a month off the calendar is also a "show me this" — it moves you to the
  // breakdown, which is where the answer is. Releasing it leaves you there.
  const setMonth = useCallback(
    (next: string | null) =>
      setSearchParams((p) =>
        write(p, { ...at(p), month: next, path: aboveTheCut(p), tab: next ? 'breakdown' : readTab(p) })),
    [setSearchParams, axis, basis]
  )
  const setTab = useCallback(
    (next: Tab) => setSearchParams((p) => write(p, { ...at(p), tab: next })),
    [setSearchParams, axis, basis]
  )
  return { setPath, descend, setAxis, setBasis, setMonth, setTab }
}

export function useOrderBookNav(): OrderBookNav {
  const [searchParams, setSearchParams] = useSearchParams()
  const rawPath = searchParams.get(PATH_KEY)
  const rawAxis = searchParams.get(AXIS_KEY)
  const axis: Axis = AXES.includes(rawAxis as Axis) ? (rawAxis as Axis) : 'customer'
  const basis: Basis = searchParams.get(BASIS_KEY) === 'std' ? 'std' : 'actual'
  const month = readMonth(searchParams)
  const path = useMemo(() => parsePath(rawPath), [rawPath])
  return { path, axis, basis, month, tab: readTab(searchParams), ...useNavSetters(axis, basis, setSearchParams) }
}
